// Componente reutilizable de contador regresivo con actualización adaptativa
// Se actualiza automáticamente según el tiempo restante:
// - >24h: cada 1 hora
// - 1-24h: cada 1 minuto  
// - <1h: cada 1 segundo

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateTimeLeft } from '../utils/propertyHelpers';

/**
 * Componente de contador regresivo adaptativo
 * @param {string} deadline - Fecha límite en formato ISO
 * @param {string} className - Clases CSS opcionales para el span
 * @returns {JSX.Element} - Tiempo restante formateado
 */
export const CountdownTimer = ({ deadline, className = '' }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(deadline, t));

  useEffect(() => {
    // Función para actualizar el tiempo
    const updateTime = () => {
      setTimeLeft(calculateTimeLeft(deadline, t));
    };

    // Calcular cuánto tiempo falta para determinar el intervalo
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;

    // Si ya finalizó, no hacer polling
    if (diff < 0) {
      setTimeLeft(t('time.finished'));
      return;
    }

    // Determinar intervalo según tiempo restante
    let intervalTime;
    
    if (diff > 24 * 60 * 60 * 1000) {
      // Más de 24 horas → actualizar cada 1 hora
      intervalTime = 60 * 60 * 1000;
    } else if (diff > 60 * 60 * 1000) {
      // Entre 1 y 24 horas → actualizar cada 1 minuto
      intervalTime = 60 * 1000;
    } else {
      // Menos de 1 hora → actualizar cada 1 segundo
      intervalTime = 1000;
    }

    // Actualización inicial inmediata
    updateTime();

    // Configurar intervalo
    const interval = setInterval(updateTime, intervalTime);

    // Cleanup al desmontar o cuando cambie el deadline
    return () => clearInterval(interval);
  }, [deadline, t]);

  return <span className={className}>{timeLeft}</span>;
};

export default CountdownTimer;